import { supabase } from '../lib/supabase';
import { DEFAULT_PROJECT_FOLDERS } from '../constants/defaultProjectFolders';

const BUCKET = 'project-documents';

/**
 * Sanitize a file name to be safe for Supabase Storage keys.
 * Removes accents, replaces spaces with underscores, strips special chars.
 */
function sanitizeFilename(name) {
  return name
    ?.normalize('NFD')
    ?.replace(/[\u0300-\u036f]/g, '')
    ?.toLowerCase()
    ?.replace(/\s+/g, '_')
    ?.replace(/[^a-z0-9_.]/g, '');
}

/**
 * Create a folder inside a project.
 * @param {string} project_id - UUID of the project
 * @param {string} name - Folder name
 * @param {string|null} parent_id - UUID of parent folder (null for root)
 * @returns {Promise<{data, error}>}
 */
export async function createFolder(project_id, name, parent_id = null) {
  const { data, error } = await supabase?.from('project_folders')?.insert({ project_id, name, parent_id })?.select()?.single();

  if (error) {
    console.error('createFolder error:', error);
  }
  return { data, error };
}

/**
 * Upload a file to a project folder.
 * Storage path: project_id/folder_id/file_name
 * @param {string} project_id - UUID of the project
 * @param {string} folder_id - UUID of the target folder
 * @param {File} file - Browser File object
 * @returns {Promise<{data, error}>}
 */
export async function uploadFile(project_id, folder_id, file) {
  const safeName = sanitizeFilename(file?.name);
  const filePath = `${project_id}/${folder_id}/${safeName}`;

  // Upload to storage
  const { error: storageError } = await supabase?.storage?.from(BUCKET)?.upload(filePath, file, { upsert: true });

  if (storageError) {
    console.error('uploadFile storage error:', storageError);
    return { data: null, error: storageError };
  }

  // Get current user
  const { data: { user } } = await supabase?.auth?.getUser();

  // Insert file record into project_files
  const { data, error } = await supabase?.from('project_files')?.insert({
      project_id,
      folder_id,
      file_name: file?.name,
      file_path: filePath,
      file_size: file?.size,
      file_type: file?.type,
      uploaded_by: user?.id ?? null,
    })?.select()?.single();

  if (error) {
    console.error('uploadFile db insert error:', error);
  }
  return { data, error };
}

/**
 * List all folders for a project.
 * @param {string} project_id - UUID of the project
 * @returns {Promise<{data, error}>}
 */
export async function listFolders(project_id) {
  const { data, error } = await supabase?.from('project_folders')?.select('*')?.eq('project_id', project_id)?.order('name', { ascending: true });

  if (error) {
    console.error('listFolders error:', error);
  }
  return { data, error };
}

/**
 * List all files inside a folder.
 * @param {string} folder_id - UUID of the folder
 * @returns {Promise<{data, error}>}
 */
export async function listFiles(folder_id) {
  const { data, error } = await supabase?.from('project_files')?.select('*')?.eq('folder_id', folder_id)?.order('created_at', { ascending: false });

  if (error) {
    console.error('listFiles error:', error);
  }
  return { data, error };
}

/**
 * Get a signed URL for a private file.
 * @param {string} file_path - Storage path of the file
 * @param {number} expiresIn - Seconds until URL expires (default 3600)
 * @returns {Promise<{signedUrl, error}>}
 */
export async function getFileSignedUrl(file_path, expiresIn = 3600) {
  const { data, error } = await supabase?.storage?.from(BUCKET)?.createSignedUrl(file_path, expiresIn);

  if (error) {
    console.error('getFileSignedUrl error:', error);
  }
  return { signedUrl: data?.signedUrl ?? null, error };
}

/**
 * Delete a file from storage and the database.
 * @param {string} file_id - UUID of the file record
 * @param {string} file_path - Storage path of the file
 * @returns {Promise<{error}>}
 */
export async function deleteFile(file_id, file_path) {
  const { error: storageError } = await supabase?.storage?.from(BUCKET)?.remove([file_path]);

  if (storageError) {
    console.error('deleteFile storage error:', storageError);
    return { error: storageError };
  }

  const { error } = await supabase?.from('project_files')?.delete()?.eq('id', file_id);

  if (error) {
    console.error('deleteFile db error:', error);
  }
  return { error };
}

/**
 * Delete a folder and all its contents (files + sub-folders cascade via DB).
 * @param {string} folder_id - UUID of the folder
 * @returns {Promise<{error}>}
 */
export async function deleteFolder(folder_id) {
  const { error } = await supabase?.from('project_folders')?.delete()?.eq('id', folder_id);

  if (error) {
    console.error('deleteFolder error:', error);
  }
  return { error };
}

/**
 * Rename a file (update only file_name in DB, storage path unchanged).
 * @param {string} file_id - UUID of the file record
 * @param {string} new_name - New display name for the file
 * @returns {Promise<{data, error}>}
 */
export async function renameFile(file_id, new_name) {
  const { data, error } = await supabase
    ?.from('project_files')
    ?.update({ file_name: new_name })
    ?.eq('id', file_id)
    ?.select()
    ?.single();

  if (error) {
    console.error('renameFile error:', error);
  }
  return { data, error };
}

/**
 * Seed the default folder structure into a newly created project.
 * Best-effort: if seeding fails, the project itself is unaffected.
 * Idempotency guard: skips silently if the project already has folders.
 *
 * @param {string} projectId - UUID of the newly created project
 * @returns {Promise<void>}
 */
export async function seedDefaultFolders(projectId) {
  try {
    // ── Idempotency guard ─────────────────────────────────────
    const { count, error: countError } = await supabase
      ?.from('project_folders')
      ?.select('id', { count: 'exact', head: true })
      ?.eq('project_id', projectId);

    if (countError) {
      console.warn('[seedDefaultFolders] Could not check existing folders:', countError?.message);
      // Proceed cautiously — do not seed if we can't verify
      return;
    }

    if (count > 0) {
      // Project already has folders — skip silently (idempotency)
      return;
    }

    // ── Step 1: Insert 3 top-level (root) folders ─────────────
    const parentRows = DEFAULT_PROJECT_FOLDERS?.map((folder) => ({
      project_id: projectId,
      name: folder?.name_fr,
      parent_id: null,
    }));

    const { data: insertedParents, error: parentError } = await supabase
      ?.from('project_folders')
      ?.insert(parentRows)
      ?.select('id, name');

    if (parentError) {
      console.error('[seedDefaultFolders] Failed to insert parent folders:', parentError?.message);
      return;
    }

    // ── Step 2: Map parent names → their generated IDs ────────
    // insertedParents order is not guaranteed to match parentRows order,
    // so we build a name→id map for safe lookup.
    const parentIdByName = {};
    insertedParents?.forEach((row) => {
      parentIdByName[row?.name] = row?.id;
    });

    // ── Step 3: Build child rows with correct parent_id ───────
    const childRows = [];
    DEFAULT_PROJECT_FOLDERS?.forEach((folder) => {
      const parentId = parentIdByName?.[folder?.name_fr];
      if (!parentId) return; // should never happen, but guard anyway
      folder?.children?.forEach((child) => {
        childRows?.push({
          project_id: projectId,
          name: child?.name_fr,
          parent_id: parentId,
        });
      });
    });

    if (childRows?.length > 0) {
      const { error: childError } = await supabase
        ?.from('project_folders')
        ?.insert(childRows);

      if (childError) {
        console.error('[seedDefaultFolders] Failed to insert child folders:', childError?.message);
        // Parent folders were already inserted — partial seed, but project is intact
      }
    }
  } catch (err) {
    console.error('[seedDefaultFolders] Unexpected error during folder seeding:', err);
    // Never throw — seeding is best-effort
  }
}
